/**
 * React component that shows the content editor options to edit the
 * metadata of a scratchpad.
 */

import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {Id, View} from "@khanacademy/wonder-blocks-core";
import {OptionItem, SingleSelect} from "@khanacademy/wonder-blocks-dropdown";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedLabeledField from "#shared-components-package/deprecated-labeled-field.tsx";
import KUITextArea from "#shared-components-package/labeled-textarea.tsx";

const CATEGORIES = [
    {value: "", label: "Regular"},
    {value: "documentation", label: "Documentation"},
    {value: "official", label: "Official Contest"},
];

type Props = {
    // Called when the close button is clicked
    onCancel?: () => unknown;
    // Called when the Save button is clicked
    onSaveAndClose: () => unknown;
    // The model this settings dialog is editing
    scratchpad: any;
};

class DevControls extends React.Component<Props> {
    handleChange: (prop: string, val?: any) => void = (
        prop: string,
        val: any,
    ) => {
        this.props.scratchpad.set(prop, val);
        this.forceUpdate();
    };

    renderModal(idRoot: string): React.ReactNode {
        const categoryOptions = CATEGORIES.map((cat, ind) => (
            <OptionItem key={ind} label={cat.label} value={cat.value} />
        ));

        const scratchpad = this.props.scratchpad;
        const catLabel = `${idRoot}label`;
        const catSelect = `${idRoot}select`;

        return (
            <OnePaneDialog
                title="Settings"
                content={
                    <View>
                        <div className="control-group">
                            <LabelMedium
                                tag="label"
                                id={catLabel}
                                htmlFor={catSelect}
                                style={styles.label}
                            >
                                Category
                            </LabelMedium>
                            <SingleSelect
                                aria-labelledby={catLabel}
                                id={catSelect}
                                placeholder="Choose a category"
                                selectedValue={scratchpad.get("category") || ""}
                                onChange={(e) =>
                                    this.handleChange("category", e)
                                }
                                style={styles.select}
                            >
                                {categoryOptions}
                            </SingleSelect>
                        </div>

                        {/* TODO(tom) Move default value to the model layer. */}
                        <DeprecatedLabeledField
                            title="Canvas Width"
                            type="text"
                            value={(scratchpad.get("width") || "").toString()}
                            onChange={(e) =>
                                this.handleChange(
                                    "width",
                                    parseInt(e.target.value) || "",
                                )
                            }
                            onBlur={(e) =>
                                this.handleChange(
                                    "width",
                                    // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'EventTarget & Element'.
                                    parseInt(e.target.value) || 400,
                                )
                            }
                        />

                        {/* TODO(tom) Move default value to the model layer. */}
                        <DeprecatedLabeledField
                            title="Canvas Height"
                            type="text"
                            value={(scratchpad.get("height") || "").toString()}
                            onChange={(e) =>
                                this.handleChange(
                                    "height",
                                    parseInt(e.target.value) || "",
                                )
                            }
                            onBlur={(e) =>
                                this.handleChange(
                                    "height",
                                    // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'EventTarget & Element'.
                                    parseInt(e.target.value) || 400,
                                )
                            }
                        />

                        {!scratchpad.isProject() && (
                            <KUITextArea
                                title="Description"
                                value={scratchpad.get("description")}
                                onChange={(e) =>
                                    this.handleChange(
                                        "description",
                                        e.target.value,
                                    )
                                }
                            />
                        )}
                    </View>
                }
                footer={
                    <View style={styles.modalFooter}>
                        <Button kind="tertiary" onClick={this.props.onCancel}>
                            Cancel
                        </Button>
                        <Strut size={spacing.medium_16} />
                        <Button
                            kind="primary"
                            onClick={this.props.onSaveAndClose}
                        >
                            Save and Close
                        </Button>
                    </View>
                }
            />
        );
    }
    render(): React.ReactNode {
        return <Id>{(idRoot) => this.renderModal(idRoot)}</Id>;
    }
}

const styles = StyleSheet.create({
    label: {
        marginBottom: spacing.small_12,
    },
    select: {
        minWidth: 180,
        maxWidth: 220,
    },
    modalFooter: {
        flexDirection: "row",
        justifyContent: "flex-end",
    },
});

export default DevControls;
